import { ProjectTemplate } from "PosterEditor/model/project-template";
import { PosterProject, StyledPoint } from "PosterEditor/model/poster-project";
import { useTemplateStore } from "entity-store/template-store/use-template-store";
import { useGpsStore } from "entity-store/gps-store/use-gps-store";
import { useImageStore } from "entity-store/image-store/use-image-store";
import { v4 as uuid } from "uuid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useProjectStore } from "entity-store/project-store/use-project-store";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { MapLabel } from "PosterEditor/model/poster-project";

export const useDuplicateProject = () => {
  const { userID, client: supabase } = useAuthContext();

  const queryClient = useQueryClient();
  const { putProject, saveProject } = useProjectStore();
  const { copyUserGPSSource } = useGpsStore();
  const { duplicateUserImage } = useImageStore();

  const duplicateProjectMutation = useMutation({
    mutationFn: async (project: PosterProject) => {
      if (!userID || !supabase) {
        throw new Error(
          "User not authenticated or Supabase client not initialized"
        );
      }
      const newProjectID = uuid();

      // console.log("DUPLICATE PROJECT: New project id: ", newProjectID);

      ///////////////////////
      /// COPY GPS SOURCE
      ///////////////////////
      const gpsSourceID = project.gpsSourceIDs[0];
      let newGPSSourceID: string | undefined;

      // console.log("DUPLICATE PROJECT: gps source id: ", project.gpsSourceIDs);

      if (gpsSourceID) {
        newGPSSourceID = await copyUserGPSSource(gpsSourceID);
      }

      // console.log("DUPLICATE PROJECT: New GPS source id: ", newGPSSourceID);

      ///////////////////////
      /// COPY PROJECT DATA
      ///////////////////////
      const newProject: PosterProject = {
        ...project,
        id: newProjectID,
        gpsSourceIDs: newGPSSourceID ? [newGPSSourceID] : [],
        isOrdered: false,
        isInCheckout: false,
        createdAt: new Date(),
      };

      // console.log("DUPLICATE PROJECT: New project data: ", newProject);

      ///////////////////////
      /// COPY THUMBNAIL
      ///////////////////////

      const thumbnailPath = `renders/${project.id}`;
      const filename = "thumbnail";
      const extension = "png";
      const destinationPath = `renders/${newProjectID}`;
      const destinationName = "thumbnail";

      // console.log("DUPLICATE PROJECT: Thumbnail details: ", {
      //   thumbnailPath,
      //   filename,
      //   extension,
      //   destinationPath,
      //   destinationName,
      // });

      const imageURL = await duplicateUserImage(
        thumbnailPath,
        filename,
        extension,
        destinationPath,
        destinationName
      );
      if (imageURL) {
        project.thumbnail = imageURL;
      }

      // console.log("DUPLICATE PROJECT: New thumbnail URL: ", imageURL);

      ///////////////////////
      /// COPY LABEL IMAGES
      ///////////////////////
      // await putTemplate.mutateAsync(newTemplate);
      // return newTemplate;
      let points = project.styledPoints as StyledPoint[];
      const updatedPoints: StyledPoint[] = await Promise.all(
        points.map(async (point) => {
          if (!point.label || !point.label.style.imageName) return point;

          const imageURL = await copyLabelImage(
            point,
            project.id,
            newProjectID
          );
          if (imageURL) {
            const newLabel: MapLabel = {
              ...point.label,
              style: {
                ...point.label.style,
                imageName: imageURL,
              },
            };
            return { ...point, label: newLabel };
          }
          return point;
        })
      );
      newProject.styledPoints = updatedPoints;

      // console.log(
      //   "DUPLICATE PROJECT: Updated points with label images: ",
      //   updatedPoints.length
      // );

      saveProject(newProject, true);

      return newProject;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });

  const copyLabelImage = async (
    point: StyledPoint,
    projectID: string,
    newProjectID: string
  ) => {
    let label = point.label;
    let imageName = point.label?.style.imageName;
    if (!label || !imageName) return;

    const originPath = `projects/${projectID}/labels`;
    const filename = imageName;
    const extension = "jpeg";
    const destinationPath = `projects/${newProjectID}/labels`;
    const newName = imageName;
    // console.log("DUPLICATE PROJECT: Copying label image with details: ", {
    //   originPath,
    //   filename,
    //   extension,
    //   destinationPath,
    //   newName,
    // });

    const imageURL = await duplicateUserImage(
      originPath,
      filename,
      extension,
      destinationPath,
      newName
    );
    return imageURL;
  };

  return {
    duplicateProject: duplicateProjectMutation,
  };
};
